import { useState, FormEvent } from 'react';
import { useTheme } from '@/context/ThemeContext';

const FORM_URL = 'https://readdy.ai/api/form/d77svi32hsar0k5m0160';

const industries = [
  'Legal Services',
  'Brokerage & Capital Markets',
  'Trade Services',
  'Clinical Practices',
  'Med Spas',
  'Other',
];

export default function Contact() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    if (charCount > 500) return;

    const body = new URLSearchParams();
    data.forEach((value, key) => {
      body.append(key, value.toString());
    });

    setStatus('loading');
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className={`py-28 px-6 md:px-10 transition-colors duration-500 ${
      isDark ? 'bg-[#0d2847]' : 'bg-[#f8f9fb]'
    }`}>
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-16">
          {/* Left: Info */}
          <div className="lg:w-[45%] shrink-0">
            <span className={`text-xs font-medium tracking-widest uppercase mb-4 block ${
              isDark ? 'text-[#14b8c8]' : 'text-[#0d7a8a]'
            }`}>
              GET IN TOUCH
            </span>
            <h2 className={`text-4xl md:text-5xl font-bold mb-6 leading-tight ${
              isDark ? 'text-white' : 'text-[#0b1f3a]'
            }`}>
              Your Business Deserves<br />
              <span className={`font-thin ${isDark ? 'text-white/50' : 'text-[#0b1f3a]/50'}`}>A Foundation That Holds</span>
            </h2>
            <p className={`text-base leading-relaxed mb-10 max-w-md ${
              isDark ? 'text-white/50' : 'text-slate-500'
            }`}>
              We build that foundation with intelligence, structure, and purpose. Let&apos;s design the system your operations require.
            </p>

            {/* Contact details */}
            <div className="flex flex-col gap-5 mb-10">
              {[
                { icon: 'ri-mail-line', label: 'Email', value: 'contact@cornerstoneai.com' },
                { icon: 'ri-phone-line', label: 'Phone', value: '+1 (555) 847-2900' },
                { icon: 'ri-time-line', label: 'Response Time', value: 'Within 24 hours' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-4">
                  <div className={`w-10 h-10 flex items-center justify-center border rounded-lg shrink-0 ${
                    isDark
                      ? 'bg-[#14b8c8]/10 border-[#14b8c8]/20'
                      : 'bg-[#0d7a8a]/10 border-[#0d7a8a]/20'
                  }`}>
                    <i className={`${item.icon} text-base ${isDark ? 'text-[#14b8c8]' : 'text-[#0d7a8a]'}`}></i>
                  </div>
                  <div>
                    <div className={`text-xs ${isDark ? 'text-white/40' : 'text-slate-400'}`}>{item.label}</div>
                    <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#0b1f3a]'}`}>{item.value}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Quote */}
            <div className={`border-l-2 pl-5 ${isDark ? 'border-[#14b8c8]' : 'border-[#0d7a8a]'}`}>
              <p className={`text-sm leading-relaxed italic ${isDark ? 'text-white/50' : 'text-slate-500'}`}>
                &ldquo;Every business has a cornerstone — the element that holds everything together. For modern organizations, that cornerstone is intelligent infrastructure.&rdquo;
              </p>
            </div>
          </div>

          {/* Right: Form */}
          <div className="flex-1">
            <div className={`rounded-2xl border p-8 md:p-10 ${
              isDark
                ? 'bg-[#0b1f3a] border-white/10'
                : 'bg-white border-slate-200'
            }`}>
              {status === 'success' ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className={`w-16 h-16 flex items-center justify-center rounded-full mb-6 ${
                    isDark ? 'bg-[#14b8c8]/10' : 'bg-[#0d7a8a]/10'
                  }`}>
                    <i className={`ri-check-line text-3xl ${isDark ? 'text-[#14b8c8]' : 'text-[#0d7a8a]'}`}></i>
                  </div>
                  <h3 className={`font-bold text-2xl mb-3 ${isDark ? 'text-white' : 'text-[#0b1f3a]'}`}>Message Received</h3>
                  <p className={`text-base max-w-sm ${isDark ? 'text-white/50' : 'text-slate-500'}`}>
                    Thank you for reaching out. Our team will review your inquiry and respond within 24 hours.
                  </p>
                  <button
                    onClick={() => setStatus('idle')}
                    className={`mt-8 px-6 py-3 rounded-full border text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      isDark
                        ? 'border-white/20 text-white/70 hover:border-[#14b8c8]/40 hover:text-[#14b8c8]'
                        : 'border-slate-300 text-slate-600 hover:border-[#0d7a8a]/40 hover:text-[#0d7a8a]'
                    }`}
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-5"
                >
                  <div className="flex flex-col sm:flex-row gap-5">
                    <div className="flex-1 flex flex-col gap-1.5">
                      <label className={`text-xs font-medium uppercase tracking-wide ${
                        isDark ? 'text-white/50' : 'text-slate-500'
                      }`}>Full Name *</label>
                      <input
                        type="text"
                        name="full_name"
                        required
                        placeholder="John Smith"
                        className={`border rounded-lg px-4 py-3 text-sm transition-colors focus:outline-none ${
                          isDark
                            ? 'bg-[#0d2847] border-white/10 text-white placeholder-white/30 focus:border-[#14b8c8]/60'
                            : 'bg-slate-50 border-slate-200 text-[#0b1f3a] placeholder-slate-300 focus:border-[#0d7a8a]/60'
                        }`}
                      />
                    </div>
                    <div className="flex-1 flex flex-col gap-1.5">
                      <label className={`text-xs font-medium uppercase tracking-wide ${
                        isDark ? 'text-white/50' : 'text-slate-500'
                      }`}>Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="john@company.com"
                        className={`border rounded-lg px-4 py-3 text-sm transition-colors focus:outline-none ${
                          isDark
                            ? 'bg-[#0d2847] border-white/10 text-white placeholder-white/30 focus:border-[#14b8c8]/60'
                            : 'bg-slate-50 border-slate-200 text-[#0b1f3a] placeholder-slate-300 focus:border-[#0d7a8a]/60'
                        }`}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-5">
                    <div className="flex-1 flex flex-col gap-1.5">
                      <label className={`text-xs font-medium uppercase tracking-wide ${
                        isDark ? 'text-white/50' : 'text-slate-500'
                      }`}>Company Name</label>
                      <input
                        type="text"
                        name="company_name"
                        placeholder="Your Company"
                        className={`border rounded-lg px-4 py-3 text-sm transition-colors focus:outline-none ${
                          isDark
                            ? 'bg-[#0d2847] border-white/10 text-white placeholder-white/30 focus:border-[#14b8c8]/60'
                            : 'bg-slate-50 border-slate-200 text-[#0b1f3a] placeholder-slate-300 focus:border-[#0d7a8a]/60'
                        }`}
                      />
                    </div>
                    <div className="flex-1 flex flex-col gap-1.5">
                      <label className={`text-xs font-medium uppercase tracking-wide ${
                        isDark ? 'text-white/50' : 'text-slate-500'
                      }`}>Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="+1 (555) 000-0000"
                        className={`border rounded-lg px-4 py-3 text-sm transition-colors focus:outline-none ${
                          isDark
                            ? 'bg-[#0d2847] border-white/10 text-white placeholder-white/30 focus:border-[#14b8c8]/60'
                            : 'bg-slate-50 border-slate-200 text-[#0b1f3a] placeholder-slate-300 focus:border-[#0d7a8a]/60'
                        }`}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className={`text-xs font-medium uppercase tracking-wide ${
                      isDark ? 'text-white/50' : 'text-slate-500'
                    }`}>Industry *</label>
                    <select
                      name="industry"
                      required
                      className={`border rounded-lg px-4 py-3 text-sm transition-colors focus:outline-none cursor-pointer ${
                        isDark
                          ? 'bg-[#0d2847] border-white/10 text-white focus:border-[#14b8c8]/60'
                          : 'bg-slate-50 border-slate-200 text-[#0b1f3a] focus:border-[#0d7a8a]/60'
                      }`}
                    >
                      <option value="">Select your industry</option>
                      {industries.map((ind) => (
                        <option key={ind} value={ind}>{ind}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center justify-between">
                      <label className={`text-xs font-medium uppercase tracking-wide ${
                        isDark ? 'text-white/50' : 'text-slate-500'
                      }`}>Tell Us About Your Needs</label>
                      <span className={`text-xs ${charCount > 500 ? 'text-red-400' : isDark ? 'text-white/40' : 'text-slate-400'}`}>{charCount}/500</span>
                    </div>
                    <textarea
                      name="message"
                      rows={4}
                      placeholder="Describe your current operational challenges and what you're looking to achieve..."
                      maxLength={500}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className={`border rounded-lg px-4 py-3 text-sm transition-colors focus:outline-none resize-none ${
                        isDark
                          ? 'bg-[#0d2847] border-white/10 text-white placeholder-white/30 focus:border-[#14b8c8]/60'
                          : 'bg-slate-50 border-slate-200 text-[#0b1f3a] placeholder-slate-300 focus:border-[#0d7a8a]/60'
                      }`}
                    ></textarea>
                  </div>

                  {status === 'error' && (
                    <p className="text-red-500 text-sm">Something went wrong. Please try again.</p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'loading' || charCount > 500}
                    className={`w-full flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold text-base transition-colors duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed ${
                      isDark
                        ? 'bg-[#14b8c8] text-[#0b1f3a] hover:bg-[#12a8b8]'
                        : 'bg-[#0d7a8a] text-white hover:bg-[#0a6878]'
                    }`}
                  >
                    {status === 'loading' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        Build My Foundation
                        <i className="ri-arrow-right-line"></i>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}